import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { PresentationChartLineIcon } from "@heroicons/react/24/solid";
import DigitalAssetFormFields from "./DigitalAssetFormFields";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import CustomLoader from "../../../../components/CustomLoader";
import { toast } from "../../../../components/toastNotification";
import type {
  DigitalAsset,
  DigitalAssetFormValues,
  DigitalAssetMode,
  OfficeOption,
  SelectOption,
} from "../types";
import { toApiPayload, toFormValues } from "../types";

interface DigitalAssetFormProps {
  asset: DigitalAsset | null;
  mode: Extract<DigitalAssetMode, "add" | "edit">;
  offices: OfficeOption[];
  services: SelectOption[];
  onlineDirectories: SelectOption[];
  appleCategories: SelectOption[];
  appleAppNames: SelectOption[];
  isLoadingOptions: boolean;
  isSaving: boolean;
  initialOfficeCode?: string;
  currentOffice?: OfficeOption;
  lockOffice?: boolean;
  onSubmit: (values: DigitalAssetFormValues) => void;
  onCancel: () => void;
}
const DigitalAssetForm = ({
  asset,
  mode,
  offices,
  services,
  onlineDirectories,
  appleCategories,
  appleAppNames,
  isLoadingOptions,
  isSaving,
  initialOfficeCode,
  currentOffice,
  lockOffice = false,
  onSubmit,
  onCancel,
}: DigitalAssetFormProps) => {
  const isAdd = mode === "add";
  const isEdit = mode === "edit";
 
  const officeOptions = useMemo(() => {
    if (!lockOffice || !currentOffice) {
      return offices;
    }

    const exists = offices.some(
      (office) => office.officeCode === currentOffice.officeCode,
    );

    return exists ? offices : [currentOffice, ...offices];
  }, [offices, currentOffice, lockOffice]);

  const [saveModal, setSaveModal] = useState(false);
  const [pendingData, setPendingData] = useState<DigitalAssetFormValues | null>(
    null,
  );
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const originalValues = useMemo(() => toFormValues(asset), [asset]);
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
  } = useForm<DigitalAssetFormValues>({ defaultValues: originalValues });
  useEffect(() => {
    if (!isEdit) {
      return;
    }
    reset(toFormValues(asset));
  }, [asset, isEdit, reset]);
  useEffect(() => {
    if (!isAdd) {
      return;
    }
    reset(toFormValues(null));
  }, [isAdd, reset]);

  useEffect(() => {
    if (!isAdd || !initialOfficeCode) {
      return;
    }

    const office =
      (lockOffice ? currentOffice : undefined) ??
      officeOptions.find((item) => item.officeCode === initialOfficeCode);

    if (!office) {
      return;
    }
    const storeNumber = office.storeNumber ?? "";
    setValue("officeName", office.officeName);
    setValue("officeCode", office.officeCode);
    setValue("storeNumber", storeNumber);
    setValue("agency", office.agency ?? office.brandMarketingName ?? "");
    setValue("officeStatus", office.officeStatus ?? "");
    setValue("officeId", office.officeId);
    setValue("name", `DA-${String(storeNumber).padStart(5, "0")}`);
  }, [
    isAdd,
    initialOfficeCode,
    currentOffice,
    lockOffice,
    officeOptions,
    setValue,
  ]);

  const submitForm = (data: DigitalAssetFormValues) => {
    if (isEdit) {
      const currentPayload = toApiPayload(data);
      const originalPayload = toApiPayload(originalValues);
      if (JSON.stringify(currentPayload) === JSON.stringify(originalPayload)) {
        toast.fire("Saving Failed", "No changes have been made", "warning");
        return;
      }
    }
    setPendingData(data);
    setSaveModal(true);
  };
  const handleSave = () => {
    if (!pendingData || isSaving) {
      return;
    }
    setSaveModal(false);
    onSubmit(pendingData);
  };
  const handleCancel = () => {
    reset();
    clearErrors();
    setPendingData(null);
    setSaveModal(false);
    onCancel();
  };
  return (
    <section className="px-8 py-4 min-h-fit">
      {!isLoadingOptions ? (
        <div>
          <div className="px-4 py-2 space-y-2 border-l-2 border-primary box-shadow">
            <form onSubmit={handleSubmit(submitForm)}>
              <div className="flex flex-row justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
                <div className="font-bold text-primary text-base">
                  {isAdd ? (
                    "Add Digital Assets"
                  ) : (
                    <div className="flex gap-2 items-center">
                      <div>
                        <PresentationChartLineIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                      </div>
                      <div className="flex flex-col leading-tight">
                        <h3 className="text-xs font-medium text-gray-500">
                          Edit Digital Asset
                        </h3>
                        <h1 className="mr-auto font-bold text-primary text-md uppercase">
                          {asset?.name}
                        </h1>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between gap-2">
                  <SubmitHookForm
                    type="submit"
                    buttonText={isSaving ? "Saving.." : "Save"}
                    onClick={() => {}}
                  />
                  <CancelButton onClick={handleCancel} />
                </div>
              </div>
              <div className="border-b-2 border-secondary" />
              <div className="my-2 text-xs text-secondary italic font-normal">
                <b>Note:</b> All fields with * are required to fill.
              </div>
              <DigitalAssetFormFields
                isEdit={isEdit}
                lockOffice={lockOffice}
                offices={officeOptions}
                services={services}
                onlineDirectories={onlineDirectories}
                appleCategories={appleCategories}
                appleAppNames={appleAppNames}
                isLoadingOptions={isLoadingOptions}
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
              />
            </form>
          </div>
          <ConfirmBoxModal
            cancelButtonRef={cancelButtonRef}
            ModalTitle={isAdd ? "Add Digital Assets" : "Edit Digital Assets"}
            qConfirmation={`Are you sure you want to ${isAdd ? "save " : "update "} this data?`}
            isOpen={saveModal}
            onClose={() => setSaveModal(false)}
            onClickFunction={handleSave}
          />
        </div>
      ) : (
        <div className="h-96">
          <CustomLoader />
        </div>
      )}
    </section>
  );
};
export default DigitalAssetForm;
